/*
 * Zorevella quick size picker
 *
 * Dawn's .card__inner carries transform: perspective(0), which creates its own
 * stacking context. Anything placed inside it is trapped below the invisible
 * full card link, no matter what z-index it gets. So the strip is attached to
 * .card-wrapper instead and measured against the photo, and it opens with
 * max-height rather than translate so it never spills past it.
 *
 * Desktop opens the strip on hover. Touch has no hover, so it used to sit open
 * permanently and ate the bottom of every photograph. On touch it now stays
 * closed behind a small Sizes tab in the corner of the image, and only the
 * card the shopper tapped is open at a time.
 */
(function () {
  const cache = {};
  let styled = false;
  let pending = false;
  const touch = window.matchMedia('(hover:none)').matches;

  function injectStyles() {
    if (styled) return;
    styled = true;
    const style = document.createElement('style');
    style.textContent =
      '.zv-sizes{position:absolute;z-index:5;overflow:hidden;max-height:0;opacity:0;' +
      'background:rgba(255,255,255,.94);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);' +
      'pointer-events:none;transition:max-height .3s cubic-bezier(.2,.7,.3,1),opacity .2s ease}' +
      '.zv-sizes__row{display:flex;align-items:stretch;justify-content:center;' +
      'border-top:1px solid rgba(0,0,0,.07)}' +
      '@media (hover:hover){.card-wrapper:hover .zv-sizes,.card-wrapper:focus-within .zv-sizes' +
      '{max-height:60px;opacity:1;pointer-events:auto}}' +
      '.zv-sizes__btn{position:relative;flex:1 1 0;min-width:0;padding:13px 4px;border:0;background:none;' +
      'cursor:pointer;font-family:var(--ai-font-accent,var(--font-body-family));font-size:1.15rem;' +
      'line-height:1;letter-spacing:.12rem;text-transform:uppercase;color:#111;font-weight:400;' +
      'transition:background .18s ease,color .18s ease,letter-spacing .18s ease}' +
      '.zv-sizes__btn span{display:inline-block;pointer-events:none;transition:transform .18s ease}' +
      '.zv-sizes__btn+.zv-sizes__btn::before{content:"";position:absolute;left:0;top:22%;bottom:22%;' +
      'width:1px;background:rgba(0,0,0,.12);transition:opacity .18s ease}' +
      '.zv-sizes__btn:hover:not([disabled]),.zv-sizes__btn:focus-visible:not([disabled])' +
      '{background:#111;color:#fff;font-weight:700;letter-spacing:.18rem;outline:none}' +
      '.zv-sizes__btn:hover:not([disabled]) span,.zv-sizes__btn:focus-visible:not([disabled]) span' +
      '{transform:scale(1.12)}' +
      '.zv-sizes__btn:hover:not([disabled])::before,' +
      '.zv-sizes__btn:hover:not([disabled])+.zv-sizes__btn::before{opacity:0}' +
      '.zv-sizes__btn.is-selected{background:#111;color:#fff;font-weight:700}' +
      '.zv-sizes__btn[disabled]{color:rgba(0,0,0,.28);cursor:not-allowed;text-decoration:line-through}' +
      '.zv-sizes__btn.is-loading{opacity:.45;pointer-events:none}' +
      '.zv-sizes-toggle{display:none}' +
      '@media (hover:none){' +
      '.zv-sizes{background:rgba(255,255,255,.96)}' +
      '.zv-sizes__btn{padding:11px 3px;font-size:1.05rem;letter-spacing:.06rem}' +
      '.card-wrapper.zv-open .zv-sizes{max-height:60px;opacity:1;pointer-events:auto}' +
      '.zv-sizes-toggle{position:absolute;z-index:6;display:inline-flex;align-items:center;' +
      'justify-content:center;margin:0;padding:6px 9px;border:0;border-radius:0;' +
      'background:rgba(255,255,255,.86);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);' +
      'font-family:var(--ai-font-accent,var(--font-body-family));font-size:.95rem;line-height:1;' +
      'letter-spacing:.1rem;text-transform:uppercase;color:#111;cursor:pointer;' +
      'transition:opacity .2s ease}' +
      '.card-wrapper.zv-open .zv-sizes-toggle{opacity:0;pointer-events:none}' +
      '}' +
      '@media (prefers-reduced-motion:reduce){.zv-sizes,.zv-sizes__btn,.zv-sizes__btn span,' +
      '.zv-sizes-toggle{transition:none}}';
    document.head.appendChild(style);
  }

  function handleFromCard(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function closeAll(except) {
    document.querySelectorAll('.card-wrapper.zv-open').forEach((open) => {
      if (open === except) return;
      open.classList.remove('zv-open');
      const toggle = open.querySelector('.zv-sizes-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  // Cover the photo box exactly. Rounding each edge to the NEAREST pixel can
  // land half a pixel short of the image, and that shortfall renders as a
  // hairline of photo under the strip on whichever columns happen to fall on a
  // fractional width, which is why it showed on some cards and not others.
  // Round outward on every edge instead, and overshoot the bottom by 1px into
  // the white gap above the title, where it cannot be seen.
  function place(card) {
    const row = card.querySelector('.zv-sizes');
    const media = card.querySelector('.card__inner');
    if (!row || !media) return;

    const cardBox = card.getBoundingClientRect();
    const mediaBox = media.getBoundingClientRect();

    const left = Math.floor(mediaBox.left - cardBox.left);
    const right = Math.ceil(mediaBox.right - cardBox.left);
    const bottom = Math.max(Math.floor(cardBox.bottom - mediaBox.bottom) - 1, 0);

    row.style.left = left + 'px';
    row.style.width = right - left + 'px';
    row.style.right = 'auto';
    row.style.bottom = bottom + 'px';

    const toggle = card.querySelector('.zv-sizes-toggle');
    if (toggle) {
      toggle.style.left = left + 10 + 'px';
      toggle.style.bottom = bottom + 10 + 'px';
    }
  }

  function pinVariant(card, variantId) {
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', url.pathname + url.search);
    });
  }

  function render(card, product) {
    if (card.querySelector('.zv-sizes')) return;

    const option = (product.options || []).find(
      (opt) => (opt.name || opt).toString().toLowerCase() === 'size'
    );
    if (!option) return;

    const wrap = document.createElement('div');
    wrap.className = 'zv-sizes';
    const row = document.createElement('div');
    row.className = 'zv-sizes__row';
    wrap.appendChild(row);

    product.variants.forEach((variant) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'zv-sizes__btn';
      btn.innerHTML = '<span></span>';
      btn.querySelector('span').textContent = variant.title;
      btn.setAttribute('aria-label', 'Add size ' + variant.title + ' to cart');
      if (!variant.available) {
        btn.disabled = true;
        btn.setAttribute('aria-label', 'Size ' + variant.title + ' is sold out');
      } else {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          row.querySelectorAll('.zv-sizes__btn').forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          pinVariant(card, variant.id);
          addToCart(variant.id, btn, card);
        });
      }
      row.appendChild(btn);
    });

    card.appendChild(wrap);

    // Touch has no hover to open the strip with, and tapping the card itself
    // has to keep meaning "open the product". So the strip gets its own small
    // tab rather than borrowing the card's tap.
    if (touch) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'zv-sizes-toggle';
      toggle.textContent = 'Sizes';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Show sizes for ' + product.title);
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        const open = !card.classList.contains('zv-open');
        closeAll(card);
        card.classList.toggle('zv-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        place(card);
      });
      card.appendChild(toggle);
    }

    card.addEventListener('mouseleave', function () {
      row.querySelectorAll('.zv-sizes__btn').forEach((b) => b.classList.remove('is-selected'));
    });

    place(card);
    setTimeout(() => place(card), 300);
    card.addEventListener('mouseenter', () => place(card));
    window.addEventListener('resize', () => place(card));
    window.addEventListener('load', () => place(card));
  }

  function addToCart(variantId, btn, card) {
    if (pending) return;
    pending = true;
    btn.classList.add('is-loading');

    const body = { items: [{ id: variantId, quantity: 1 }] };
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (drawer) {
          drawer.classList.remove('is-empty');
          const inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
          const items = drawer.querySelector('cart-drawer-items');
          if (items) items.classList.remove('is-empty');
        }

        if (drawer && response.sections) drawer.renderContents(response);
        if (window.ZvCartTab) window.ZvCartTab.refresh();
        closeAll(null);
      })
      .catch(() => {
        const handle = card && card.dataset ? card.dataset.zvHandle : null;
        if (handle) window.location = '/products/' + handle + '?variant=' + variantId;
      })
      .finally(() => {
        btn.classList.remove('is-loading');
        pending = false;
      });
  }

  function prepare(card) {
    if (card.dataset.zvReady) return;
    card.dataset.zvReady = '1';

    const handle = handleFromCard(card);
    if (!handle) return;
    card.dataset.zvHandle = handle;

    const load = function () {
      if (cache[handle]) {
        render(card, cache[handle]);
        return;
      }
      fetch('/products/' + handle + '.js')
        .then((response) => response.json())
        .then((product) => {
          cache[handle] = product;
          render(card, product);
        })
        .catch(() => {});
    };

    card.addEventListener('mouseenter', load, { once: true });
    card.addEventListener('focusin', load, { once: true });
    if (touch) load();
  }

  function scan() {
    const grid = document.querySelector('#product-grid, .collection .grid, #main-collection-product-grid');
    if (!grid) return;
    injectStyles();
    grid.querySelectorAll('.card-wrapper').forEach(prepare);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  window.addEventListener('popstate', () => setTimeout(scan, 400));
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.zv-sizes, .zv-sizes-toggle')) closeAll(null);
    if (event.target.closest('.facets, .facets-vertical, .pagination')) setTimeout(scan, 600);
  });
})();
